import { z } from 'zod';

import { AiRequestBaseSchema } from './ai-request';

/**
 * Vector embedding — the `embed` half of the provider interface, served by the
 * `ai-embedding` capability.
 *
 * **Text only, and that is a scope decision P13 must revisit.** The catalog's
 * only `ai-embedding` provider offers text embedding models; N19's
 * `face_embedding` signal needs an *image* input, which no configured provider
 * can do. P8 does not invent a capability with no consumer, so the honest
 * surface today is text. When P13 builds face-match it adds a `face-embedding`
 * capability with an image-capable provider — a new capability and adapter, not
 * a widening of this one, because "embed" with two incompatible input kinds is
 * a discriminated union nobody wants at a call site.
 */
export const AiEmbeddingRequestSchema = AiRequestBaseSchema.extend({
  input: z.string().min(1).max(100_000),
  /**
   * The requested vector width, when the provider supports truncation.
   *
   * Left unset the model's native width is used. It matters because a pgvector
   * column is fixed-width: the phase that stores embeddings pins this and never
   * changes it, since vectors of two widths in one column cannot be compared.
   */
  dimensions: z.number().int().min(1).max(4_096).optional(),
}).strict();

export type AiEmbeddingRequest = z.infer<typeof AiEmbeddingRequestSchema>;

export const AiEmbeddingResultSchema = z
  .object({
    vector: z.array(z.number()).min(1),
    /** Always `vector.length` — carried explicitly so a caller need not measure. */
    dimensions: z.number().int().min(1),
  })
  .strict();

export type AiEmbeddingResult = z.infer<typeof AiEmbeddingResultSchema>;
